import { v4 as uuid_v4 } from 'uuid';

import { LUXURY_ESCAPES } from '~/consts/brands';

import { type CalculateEarnRequest, calculateEarnPoints, getLoyaltyAccount } from '~/services/LuxLoyalty';

import { PURCHASE_TYPE_POST_PURCHASE, SALES_ATTRIBUTION_CS_ERROR } from './constants';

// Narrow return type to the specific values this function produces.
// All belong to the second variant of CalculateEarnRequest's productDetails
// (where offerId/packageId are optional), so TypeScript can resolve the discriminated union.
type CustomOfferLoyaltyProductType = 'hotel_lpp_eps' | 'experience' | 'cruise_group_3' | 'dom_economy';

export const mapCustomOfferTypeToLoyaltyProductType = (productType: string): CustomOfferLoyaltyProductType | null => {
  switch (productType) {
    case 'hotel':
      return 'hotel_lpp_eps';
    case 'tour':
      return 'hotel_lpp_eps';
    case 'experience':
      return 'experience';
    case 'ics_store':
    case 'le_live':
      return 'experience';
    case 'cruise':
      return 'cruise_group_3';
    case 'flight':
    case 'other':
      return 'dom_economy';
    default:
      return null;
  }
};

export interface PendingPointsItem {
  transactionKey: string;
  points: number;
  bonusPoints: number;
  statusCredits: number;
  bonusStatusCredits: number;
  appliedCustomRuleIds: Array<string>;
  metadata: {
    itemId: string;
    itemTransactionKey: string;
    title: string;
    loyaltyProductType: CustomOfferLoyaltyProductType;
    itemType: string;
    totals: Record<string, unknown>;
    pointsEarnCalculation: Record<string, unknown>;
    source: string;
    purchaseMode: string;
  };
}

export const buildPendingPointsPayload = async (
  user: App.User,
  offer: App.CustomOffer,
  transactionKey: string,
  salesAttribution?: string,
  brand?: App.Brands,
): Promise<Array<PendingPointsItem> | null> => {
  // Only Luxury Escapes brand orders should earn pending points for loyalty
  if (brand !== LUXURY_ESCAPES) {
    return null;
  }

  // If sales attribution is CS Error, no pending points should be created
  if (salesAttribution === SALES_ATTRIBUTION_CS_ERROR) {
    return null;
  }

  const metadataMargin = (offer.payment_metadata.margin ?? 0) / 100;
  const metadataFee = (offer.payment_metadata.le_service_fee ?? 0) / 100;
  const customerPrice = (offer.price ?? 0) / 100;

  const item = offer.items[0];
  if (!item) {
    return null;
  }

  // Post-purchase flights carry their markup on travellers[].le_fee, leaving metadata margin/fee at zero.
  // Quote those at customer price to match the BQ pending-earn import (custom_offer_flight).
  const isPostPurchaseFlight = item.type === 'flight' && offer.purchase_type === PURCHASE_TYPE_POST_PURCHASE;

  // Every other custom offer: unchanged — still no quote when metadata margin/fee are zero.
  if (metadataMargin <= 0 && metadataFee <= 0 && !isPostPurchaseFlight) {
    return null;
  }

  const sellPrice =
    metadataMargin > 0 || metadataFee > 0 ? offer.cost_price / 100 + metadataMargin + metadataFee : customerPrice;

  if (sellPrice <= 0) {
    return null;
  }

  const loyaltyProductType = mapCustomOfferTypeToLoyaltyProductType(item.type);
  if (!loyaltyProductType) {
    return null;
  }

  try {
    const request: CalculateEarnRequest = {
      customerId: user.id_member,
      publicPrice: sellPrice,
      luxPlusMember: false,
      currency: offer.cost_price_currency,
      productDetails: {
        loyaltyProductType,
      },
    };

    const response = await calculateEarnPoints(request);
    const result = response.result;

    // this is to replicate the cartId behaviour in the checkout
    // we do not care about the itemId as we use transactionKey to find related items
    const itemId = uuid_v4();

    return [
      {
        transactionKey,
        points: result.totalBasePoints,
        bonusPoints: result.totalBonusPoints,
        statusCredits: result.totalStatusCredits,
        bonusStatusCredits: result.totalBonusStatusCredits,
        appliedCustomRuleIds: result.appliedCustomRuleIds,
        metadata: {
          itemId,
          itemTransactionKey: transactionKey,
          title: item.name,
          loyaltyProductType,
          itemType: item.type,
          totals: {
            defaultPayableItemPrice: sellPrice,
            initialBasePoints: result.totalBasePoints,
            initialStatusCredits: result.totalStatusCredits,
            initialBonusStatusCredits: result.totalBonusStatusCredits,
            initialBonusPoints: result.totalBonusPoints,
            earnableBasePoints: result.totalBasePoints,
            earnableBonusPoints: result.totalBonusPoints,
            earnablePoints: result.totalBasePoints,
            earnableStatusCredits: result.totalStatusCredits,
            earnableBonusStatusCredits: result.totalBonusStatusCredits,
            appliedRuleIds: result.appliedCustomRuleIds,
            itemPromoDiscount: 0,
            pointsPaidInCurrency: 0,
            itemUnearnableCreditAmount: 0,
          },
          pointsEarnCalculation: {
            data: {
              request: {
                price: sellPrice,
                memberPrice: 0,
                loyaltyProductType,
              },
              points: result.totalBasePoints,
              basePoints: result.totalBasePoints,
              bonusPoints: result.totalBonusPoints,
              statusCredits: result.totalStatusCredits,
              bonusStatusCredits: result.totalBonusStatusCredits,
              appliedRuleIds: result.appliedCustomRuleIds,
              roundingEnabled: result.roundingEnabled,
              roundedPointsEarnRatio: result.totalPointsEarnRatio,
            },
            fetching: false,
          },
          source: 'Admin',
          purchaseMode: 'order-creation',
        },
      },
    ];
  } catch (error) {
    console.error('Failed to calculate loyalty points:', error);
    return null;
  }
};

export const fetchLoyaltyTier = async (customerId: string): Promise<string | null> => {
  try {
    const accountResponse = await getLoyaltyAccount(customerId);
    return accountResponse.result?.accountSummary?.tier ?? null;
  } catch (error) {
    console.error('Failed to fetch loyalty account:', error);
    return null;
  }
};
